import { FullStory } from '@fullstory/browser';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { type FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { authClient, signIn } from '~ai-tutor/lib/auth/better-auth/authClient';
import { getRegistrationUrl } from '~ai-tutor/lib/config/clientConfig';
import { getPendingRedeemToken, storePendingRedeemToken } from '~ai-tutor/lib/hooks/useAutoRedeemToken';
import {
  currentUserQueryOptions,
  loginFn,
  tokenRequiresOidcLoginFn,
} from '~ai-tutor/lib/serverFunctions/auth';
import { Route } from '~ai-tutor/routes/login';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

export const Login: FunctionComponent = () => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const [oidcLoginRequired, setOidcLoginRequired] = useState(false);

  const { alreadyAuthenticated, identityType } = Route.useRouteContext();
  const { silent_failed } = Route.useSearch();

  const queryClient = useQueryClient();

  const silentOidcStartedRef = useRef(false);

  const login = useServerFn(loginFn);
  const tokenRequiresOidcLogin = useServerFn(tokenRequiresOidcLoginFn);

  const registrationUrl = getRegistrationUrl();

  const performLoginWithToken = async (
    token: string,
    redirectTo: string = '/',
    isCancelled?: () => boolean,
  ) => {
    const skip = () => isCancelled?.() ?? false;
    try {
      const result = await login({ data: { token } });
      if (skip()) return;
      if (result.tokenRequiresOidcLogin) {
        setOidcLoginRequired(true);
        return;
      }
      if (result.success) {
        await queryClient.invalidateQueries({ queryKey: currentUserQueryOptions().queryKey });
        if (skip()) return;
        await navigate({ to: redirectTo });
      } else {
        if (skip()) return;
        setError(t('login.error.withMessage', { message: result.error }));
      }
    } catch {
      if (skip()) return;
      setError(t('login.error.generic'));
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Silent OIDC must run only in the browser (not in route beforeLoad — breaks SSR).
  useEffect(() => {
    // eslint-disable-next-line unicorn-x/no-typeof-undefined
    if (typeof globalThis.window === 'undefined') return;
    if (alreadyAuthenticated || silent_failed) return;
    if (silentOidcStartedRef.current) return;
    silentOidcStartedRef.current = true;
    void signIn.oauth2({ providerId: 'oidc-silent' });
  }, [alreadyAuthenticated, silent_failed]);

  // Single mount flow: (1) hash token login if present, (2) then redirect if already authenticated.
  // Order avoids racing redirect with hash handling / token persistence.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // eslint-disable-next-line unicorn-x/no-typeof-undefined
      if (typeof globalThis.window === 'undefined') return;

      const hash = globalThis.location.hash.slice(1);
      const hashParams = new URLSearchParams(hash);
      const token = hashParams.get('token') ?? getPendingRedeemToken();

      if (token) {
        globalThis.location.hash = '';
        storePendingRedeemToken(token);
        const isCancelled = () => cancelled;

        if (identityType === 'oidc') {
          const result = await tokenRequiresOidcLogin({ data: { token } });
          setOidcLoginRequired(result);
        } else {
          await performLoginWithToken(token, '/settings', isCancelled);
        }
      }

      if (cancelled) return;

      if (alreadyAuthenticated) {
        await navigate({ to: '/settings', replace: true });
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; beforeLoad fixes alreadyAuthenticated
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-24 p-4">
      {!oidcLoginRequired && (
        <Card className="bg-accent-subtle w-full max-w-md">
          <CardContent className="space-y-6">{t('login.withoutAccount.description')}</CardContent>
        </Card>
      )}

      <Card className="w-full max-w-md">
        <CardHeader className="mb-3">
          <CardTitle>{t('login.title')}</CardTitle>
          <CardDescription>
            <div className="mb-12">{t('login.description.login')}</div>

            <div className="mb-4">{t('login.description.register')}</div>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* OIDC sign-in */}
          <div className="flex flex-col gap-12">
            <Button
              type="button"
              variant="default"
              className="w-full"
              onClick={() => {
                FullStory('trackEvent', {
                  name: 'lernassistent.btnLogin.click',
                  properties: {},
                });

                void authClient.signIn.oauth2({
                  providerId: 'oidc',
                  callbackURL: '/settings',
                });
              }}
            >
              {t('login.buttonLogin')}
            </Button>

            <Button asChild variant="secondary" className="w-full">
              <a
                href={registrationUrl}
                onClick={() => {
                  FullStory('trackEvent', {
                    name: 'lernassistent.btnRegister.click',
                    properties: {},
                  });
                }}
              >
                {t('login.buttonRegister')}
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
